import { useSyncExternalStore } from "react";
import type { PublicAdsConfig, AdPlacement } from "@serendiptv/contracts";
import { combinedPermission, gppPermission, tcfPermission, type ConsentState, type TCData, type GppData } from "./consent";

type Queue = { push: (value: Record<string, never>) => unknown; requestNonPersonalizedAds?: number; pauseAdRequests?: number };
type ConsentCallback = Record<string, () => void> | (() => void);
type GoogleFC = {
  callbackQueue: { push: (callback: ConsentCallback) => unknown };
  showRevocationMessage?: () => void;
  usstatesoptout?: { overrideDnsLink?: boolean; getInitialUsStatesOptOutStatus?: () => number; openConfirmationDialog?: (callback: (optedOut: boolean) => void) => void };
};
declare global {
  interface Window {
    adsbygoogle?: Queue;
    googlefc?: GoogleFC;
    __tcfapi?: (command: string, version: number, callback: (data: TCData, success: boolean) => void) => void;
    __gpp?: (command: string, callback: (event: { pingData?: GppData }, success: boolean) => void) => void;
  }
}
let europe: ConsentState = "pending", us: ConsentState = "pending";
let consent: ConsentState = "pending";
let controls = { europe: false, us: false };
let gppSeen = false;
let europeApplies = false, usApplies = false, usDialogOpen = false;
let settings: PublicAdsConfig | undefined;
let publisher: string | undefined;
let loader: Promise<void> | undefined;
const subscribers = new Set<() => void>();
const notify = () => { for (const callback of subscribers) callback(); };
function updateConsent() {
  consent = combinedPermission(europe, usDialogOpen ? "pending" : us, (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true);
  if (window.adsbygoogle) {
    window.adsbygoogle.requestNonPersonalizedAds = 1;
    window.adsbygoogle.pauseAdRequests = settings?.mode === "live" && (settings.guide_enabled || settings.cabinet_enabled) && consent === "allowed" ? 0 : 1;
  }
  const nextControls = {
    europe: europeApplies && typeof window.googlefc?.showRevocationMessage === "function",
    us: usApplies && us !== "denied" && !usDialogOpen && typeof window.googlefc?.usstatesoptout?.openConfirmationDialog === "function"
  };
  if (nextControls.europe !== controls.europe || nextControls.us !== controls.us) controls = nextControls;
  notify();
}
export function configureProvider(config: PublicAdsConfig) { settings = config; updateConsent(); }
export function useConsentState() { return useSyncExternalStore(callback => { subscribers.add(callback); return () => { subscribers.delete(callback); }; }, () => consent); }
export function useConsentControls() { return useSyncExternalStore(callback => { subscribers.add(callback); return () => { subscribers.delete(callback); }; }, () => controls); }

function initializeConsent() {
  window.googlefc ??= { callbackQueue: [] as ConsentCallback[] };
  window.googlefc.callbackQueue ??= [] as ConsentCallback[];
  window.googlefc.usstatesoptout ??= {};
  window.googlefc.usstatesoptout.overrideDnsLink = true;
  window.googlefc.callbackQueue.push({ CONSENT_API_READY: () => {
    try { window.__tcfapi?.("addEventListener", 2, (data, success) => { europeApplies = success && data?.gdprApplies === true; europe = tcfPermission(data, success); updateConsent(); }); }
    catch { europe = "denied"; updateConsent(); }
    try { window.__gpp?.("addEventListener", (event, success) => { gppSeen = true; us = gppPermission(event?.pingData, success); usApplies = success && Array.isArray(event?.pingData?.applicableSections) && event.pingData.applicableSections.some(id => Number.isInteger(id) && id >= 7 && id <= 27); updateConsent(); }); }
    catch { gppSeen = true; us = "denied"; updateConsent(); }
  } });
  window.googlefc.callbackQueue.push({ INITIAL_US_STATES_OPT_OUT_DATA_READY: () => {
    // The initial status is only enough for non-applicable regions or denial.
    // Applicable US regions require the live GPP signal above, including revocation.
    let status: number | undefined;
    try { status = window.googlefc?.usstatesoptout?.getInitialUsStatesOptOutStatus?.(); }
    catch { us = "denied"; updateConsent(); }
    if (!gppSeen) {
      usApplies = status === 2 || status === 3;
      if (status === 1) us = "allowed";
      else if (status === 3) us = "denied";
    }
    updateConsent();
  } });
}

export function loadProvider(config: PublicAdsConfig): Promise<void> {
  if (config.mode !== "live" || !config.publisher_id) return Promise.reject(new Error("Display ads disabled"));
  if (publisher && publisher !== config.publisher_id) return Promise.reject(new Error("Publisher change requires a new document"));
  if (loader) return loader;
  publisher = config.publisher_id;
  // Load Google's documented ad tag paused so its account-configured Privacy &
  // messaging CMP can initialize. Waiting for that CMP before loading this tag
  // would deadlock. Pausing requests does not promise zero cookie access.
  window.adsbygoogle ??= [] as Record<string, never>[];
  window.adsbygoogle.requestNonPersonalizedAds = 1;
  window.adsbygoogle.pauseAdRequests = 1;
  initializeConsent();
  loader = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.async = true; script.crossOrigin = "anonymous";
    script.nonce = document.querySelector<HTMLMetaElement>('meta[property="csp-nonce"]')?.nonce ?? "";
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${config.publisher_id}`;
    const timeout = setTimeout(() => reject(new Error("Ad provider unavailable")), 12_000);
    script.onload = () => { clearTimeout(timeout); resolve(); };
    script.onerror = () => { clearTimeout(timeout); reject(new Error("Ad provider unavailable")); };
    document.head.append(script);
  });
  return loader;
}
export function requestAllowed(config: PublicAdsConfig, placement: AdPlacement) {
  return config.mode === "live" && settings?.mode === "live" && publisher === config.publisher_id && settings.publisher_id === config.publisher_id
    && config[`${placement}_enabled`] && settings[`${placement}_enabled`] && settings[`${placement}_unit_id`] === config[`${placement}_unit_id`]
    && consent === "allowed" && document.visibilityState !== "hidden";
}
export function openPrivacyChoices() {
  window.googlefc?.callbackQueue.push(() => { europe = "pending"; updateConsent(); window.googlefc?.showRevocationMessage?.(); });
}
export function openDoNotSell() {
  window.googlefc?.callbackQueue.push({ INITIAL_US_STATES_OPT_OUT_DATA_READY: () => {
    usDialogOpen = true; updateConsent();
    try { window.googlefc?.usstatesoptout?.openConfirmationDialog?.(optedOut => {
      usDialogOpen = false; if (optedOut) us = "denied"; updateConsent();
    }); } catch { usDialogOpen = false; us = "denied"; updateConsent(); }
  } });
}
